/**
 * Tenant access: Admin, Builder and Viewer (2026-09-29).
 *
 * This file is the single source of truth for access control in ai-manager.
 * New route, UI, Authentik, and API-token code should use only the four
 * permissions below. Retired names remain in LEGACY_TO_NEW_PERMISSION so old
 * roles, claims, sessions, and tokens normalize at read time. Agents and
 * workflows share the same run and manage capabilities.
 */

// ============================================================================
// Permission Definitions
// ============================================================================

export const PERMISSIONS = {
  'AIManager:RunAgents':
    'Browse and run existing agents of every type, including workflows, and inspect their conversations and execution results.',
  'AIManager:ManageAgents':
    'Create, edit, publish, delete, import and export agents of every type, including workflows, their triggers and saved tests.',
  'AIManager:ManageTools':
    'Manage tools, tool packs, knowledge tools, score models, external consult profiles, A2UI catalogs, surface configs, multimodal config, branding, preview embeds, and catalog import/export.',
  'AIManager:ManageConfiguration':
    'Manage tenant configuration: users, groups, settings, prompts, telephony/provider config, integrations, embed clients, allowed origins, network policy, tenant API tokens, certificates, and platform configuration.',
} as const;

export type Permission = keyof typeof PERMISSIONS;

export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

/**
 * Managing agents includes running them. Run access never implies authoring.
 */
export const PERMISSION_IMPLIES: Readonly<Record<Permission, Permission[]>> = {
  'AIManager:RunAgents': [],
  'AIManager:ManageAgents': ['AIManager:RunAgents'],
  'AIManager:ManageTools': [],
  'AIManager:ManageConfiguration': [],
};

// ============================================================================
// Back-compat: normalize retired permission strings without elevating readers
// ============================================================================

export const LEGACY_TO_NEW_PERMISSION: Readonly<Record<string, Permission[]>> = {
  // Agents and formerly read-only agent surfaces.
  'AIManager:ViewAgents': ['AIManager:RunAgents'],
  'AIManager:EditAgents': ['AIManager:ManageAgents'],
  'AIManager:ManageAgents': ['AIManager:ManageAgents'],
  'AIManager:ViewPerformance': ['AIManager:RunAgents'],

  // Workflows.
  'AIManager:ViewWorkflows': ['AIManager:RunAgents'],
  'AIManager:EditWorkflows': ['AIManager:ManageAgents'],
  'AIManager:ManageWorkflows': ['AIManager:ManageAgents'],

  // Tools, multimodal, A2UI, catalogs, and branding.
  'AIManager:ViewTools': ['AIManager:RunAgents'],
  'AIManager:ManageTools': ['AIManager:ManageTools'],
  'AIManager:ManageMultimodal': ['AIManager:ManageTools'],

  // Tenant configuration and integrations.
  'AIManager:ViewIntegrations': [],
  'AIManager:ManageIntegrations': ['AIManager:ManageConfiguration'],
  'AIManager:ManageApiTokens': ['AIManager:ManageConfiguration'],
  'AIManager:ViewSettings': [],
  'AIManager:ManageSettings': ['AIManager:ManageConfiguration'],
  'AIManager:ViewTelephony': [],
  'AIManager:ManageTelephony': ['AIManager:ManageConfiguration'],
  'AIManager:ViewScaleTesting': [],
  'AIManager:RunScaleTests': ['AIManager:ManageConfiguration'],
  'AIManager:ViewObservability': ['AIManager:RunAgents'],
  'AIManager:MakeSIPCalls': ['AIManager:RunAgents'],
  'AIManager:ManageGroups': ['AIManager:ManageConfiguration'],
};

function upgradeLegacyPermission(name: string): Permission[] {
  if (Object.hasOwn(PERMISSIONS, name)) return [name as Permission];
  return Object.hasOwn(LEGACY_TO_NEW_PERMISSION, name) ? LEGACY_TO_NEW_PERMISSION[name] : [];
}

// ============================================================================
// Role Definitions
// ============================================================================

export interface RoleDefinition {
  name: string;
  description: string;
  permissions: Permission[];
}

export const ROLES: Record<string, RoleDefinition> = {
  admin: {
    name: 'Admin',
    description: 'Full access to all AI Manager tenant capabilities.',
    permissions: ALL_PERMISSIONS,
  },
  builder: {
    name: 'Builder',
    description:
      'Build and ship agents, workflows, tools, and multimodal surfaces. No tenant configuration access.',
    permissions: [
      'AIManager:ManageAgents',
      'AIManager:ManageTools',
    ],
  },
  viewer: {
    name: 'Viewer',
    description:
      'Browse and run existing agents, including workflows, without changing their definitions or tenant configuration.',
    permissions: ['AIManager:RunAgents'],
  },
} as const;

export type RoleName = keyof typeof ROLES;

/** Keep existing SSO groups and stored memberships working during migration. */
export const LEGACY_ROLES: Readonly<Record<string, string>> = {
  developer: 'builder',
  operator: 'admin',
  analyst: 'viewer',
};

export function normalizeRoles(roles: readonly string[]): string[] {
  return Array.from(new Set(roles.flatMap((role) => {
    const current = LEGACY_ROLES[role] ?? role;
    return Object.hasOwn(ROLES, current) ? [current] : [];
  })));
}

// ============================================================================
// Tab Permission Mappings
// ============================================================================

/**
 * Maps sidebar tabs to required permissions (any-of). Users need at least one
 * listed permission to see the tab. Server routes must enforce the same or
 * stricter permission via withAuth.
 */
export const TAB_PERMISSIONS: Record<string, Permission[]> = {
  dashboard: ['AIManager:RunAgents'],
  agents: ['AIManager:RunAgents'],
  workflows: ['AIManager:RunAgents'],
  tools: ['AIManager:ManageTools'],
  multimodal: ['AIManager:ManageTools'],
  branding: ['AIManager:ManageTools'],
  settings: [],
  config: ['AIManager:ManageConfiguration'],
  integrations: ['AIManager:ManageConfiguration'],
  admin: ['AIManager:ManageConfiguration'],
  debugging: ['AIManager:RunAgents'],
  testing: ['AIManager:RunAgents'],
  'scale-testing': ['AIManager:ManageConfiguration'],
  simulation: ['AIManager:RunAgents'],
  performance: ['AIManager:RunAgents'],
  ccaas: ['AIManager:ManageConfiguration'],
  surfaces: ['AIManager:ManageTools'],
  pipelines: ['AIManager:RunAgents'],
  'realtime-translation': ['AIManager:RunAgents'],
};

// ============================================================================
// Helper Functions
// ============================================================================

export function normalizePermissions(rawPermissions: string[]): Permission[] {
  if (rawPermissions.includes('*')) return ALL_PERMISSIONS;
  const out = new Set<Permission>();
  for (const raw of rawPermissions) {
    for (const upgraded of upgradeLegacyPermission(raw)) {
      out.add(upgraded);
      for (const implied of PERMISSION_IMPLIES[upgraded]) out.add(implied);
    }
  }
  return Array.from(out);
}

/**
 * Resolve the named tenant access levels. Retired direct grants are accepted
 * as an argument for old callers but cannot override a role's capabilities.
 */
export function expandRolePermissions(
  roles: string[],
  _legacyDirectPermissions: string[] = []
): Permission[] {
  const out = new Set<Permission>();

  const add = (p: Permission) => {
    if (out.has(p)) return;
    out.add(p);
    for (const implied of PERMISSION_IMPLIES[p] ?? []) out.add(implied);
  };

  for (const roleName of normalizeRoles(roles)) {
    const role = ROLES[roleName];
    if (!role) continue;
    for (const raw of role.permissions) {
      for (const upgraded of upgradeLegacyPermission(raw)) add(upgraded);
    }
  }

  return Array.from(out);
}

export function hasPermission(
  userPermissions: string[],
  requiredPermission: Permission
): boolean {
  if (userPermissions.includes('*')) return true;
  if (userPermissions.includes(requiredPermission)) return true;
  for (const raw of userPermissions) {
    if (upgradeLegacyPermission(raw).some((permission) =>
      permission === requiredPermission || PERMISSION_IMPLIES[permission].includes(requiredPermission),
    )) return true;
  }
  return false;
}

export function hasAnyPermission(
  userPermissions: string[],
  requiredPermissions: Permission[]
): boolean {
  if (requiredPermissions.length === 0) return true;
  return requiredPermissions.some((p) => hasPermission(userPermissions, p));
}

export function hasAllPermissions(
  userPermissions: string[],
  requiredPermissions: Permission[]
): boolean {
  return requiredPermissions.every((p) => hasPermission(userPermissions, p));
}

export function getAccessibleTabs(userPermissions: string[]): string[] {
  return Object.entries(TAB_PERMISSIONS)
    .filter(([, requiredPerms]) =>
      hasAnyPermission(userPermissions, requiredPerms)
    )
    .map(([tabName]) => tabName);
}

export function getDefaultRedirectPath(userPermissions: string[]): string {
  const accessibleTabs = getAccessibleTabs(userPermissions);
  if (accessibleTabs.length === 0) return '/settings';
  const tabPriority = ['dashboard', 'agents', 'workflows', 'settings'];
  for (const tab of tabPriority) {
    if (accessibleTabs.includes(tab)) return `/${tab}`;
  }
  return `/${accessibleTabs[0]}`;
}
